import { useCallback, useRef } from "react";
import { recommendationsService } from "./service";

type InteractionType =
  | "view"
  | "click"
  | "save"
  | "like"
  | "enquiry"
  | "share"
  | "chat_kw"
  | "search";

/**
 * Fire-and-forget interaction recorder.
 *
 * All methods swallow errors — tracking must never break the buyer's UI flow.
 * Duplicate view events for the same feedId are suppressed within a
 * component's lifetime via an internal Set.
 */
export function useInteractionTracking() {
  const viewedIds = useRef(new Set<string>());

  const record = useCallback(
    (feedId: string | null | undefined, type: InteractionType, meta?: Record<string, unknown>) => {
      if (!feedId) return;
      recommendationsService
        .recordInteraction({ feed_id: feedId, interaction_type: type, meta })
        .catch(() => {});
    },
    [],
  );

  const recordView = useCallback(
    (feedId: string | null | undefined) => {
      if (!feedId || viewedIds.current.has(feedId)) return;
      viewedIds.current.add(feedId);
      record(feedId, "view");
    },
    [record],
  );

  const recordClick = useCallback(
    (feedId: string | null | undefined) => record(feedId, "click"),
    [record],
  );

  const recordSave = useCallback(
    (feedId: string | null | undefined) => record(feedId, "save"),
    [record],
  );

  const recordLike = useCallback(
    (feedId: string | null | undefined) => record(feedId, "like"),
    [record],
  );

  const recordEnquiry = useCallback(
    (feedId: string | null | undefined) => record(feedId, "enquiry"),
    [record],
  );

  const recordShare = useCallback(
    (feedId: string | null | undefined) => record(feedId, "share"),
    [record],
  );

  return { recordView, recordClick, recordSave, recordLike, recordEnquiry, recordShare };
}
